/*
 * km2-product-card.js — shared cream product-card builder for the KM2 rails.
 *
 * ONE source of truth for the .ebc-card markup + hover image-cycle + money
 * formatting, so every rail that shows product cards (EBC spotlight, recently
 * viewed, …) renders an identical card and only differs in its DATA source.
 * Styling lives in ebc-spotlight.css; the auto-scroll engine is km2-rail.js.
 *
 * Usage:
 *   var card = window.KM2ProductCard.create({ moneyFormat: {{ shop.money_format | json }} });
 *   railOpts.renderItem = function (p) { return card.build(p); };
 *
 * Expected product shape (matches the EBC GraphQL→card mapping):
 *   { title, handle, product_type, tags:[], images:[{src}],
 *     variants:[{ price, compare_at_price, available }] }   // price in shop currency units
 */
(function () {
  if (window.KM2ProductCard) return; // idempotent

  // Quick-add button icons — one span per state; CSS shows the right one via the
  // button's state class (default bag / .is-loading spinner / .is-added check / .is-error x).
  var QA_ICONS =
    '<span class="ebc-qa__i ebc-qa__bag"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 8h12l-1 12H7L6 8z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg></span>' +
    '<span class="ebc-qa__i ebc-qa__spin"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 3a9 9 0 1 0 9 9"/></svg></span>' +
    '<span class="ebc-qa__i ebc-qa__ok"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>' +
    '<span class="ebc-qa__i ebc-qa__err"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></span>';

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Type-badge label. Fragrance concentrations are abbreviated; every other non-empty
  // product_type shows AS-IS; empty returns '' (caller renders no badge node).
  // MIRRORED in snippets/card-product.liquid (the km2_frag `case` map) — change BOTH.
  // Also exported on window.KM2ProductCard so kmf-filters.js reuses this one copy.
  function typeAbbr(t) {
    var s = String(t || '').trim();
    if (!s) return '';
    var map = {
      'eau de parfum': 'EDP', 'eau de toilette': 'EDT', 'eau de cologne': 'EDC',
      'eau de senteur': 'EDS', 'parfum': 'Parfum', 'extrait de parfum': 'Extrait',
      'eau fraiche': 'Eau Fraîche', 'eau fraîche': 'Eau Fraîche', 'parfum de toilette': 'PDT'
    };
    return map[s.toLowerCase()] || s;
  }

  // Build a money() bound to the shop's Liquid money_format (passed in from the section).
  // "From $X" — the shared prefix for a card whose product has dearer in-stock sizes (finding 513).
  // Reads the locale string theme.liquid publishes (window.variantStrings.fromPrice, `[price]`
  // placeholder) so every JS-built card prints exactly what snippets/price.liquid prints; the
  // English fallback covers a page that never loaded theme.liquid's strings block.
  function fromPrice(moneyStr) {
    var tpl = (window.variantStrings && window.variantStrings.fromPrice) || 'From [price]';
    return tpl.indexOf('[price]') !== -1 ? tpl.replace('[price]', moneyStr) : tpl + ' ' + moneyStr;
  }

  function makeMoney(moneyFormat) {
    var fmt = moneyFormat || '${{amount}}';
    return function money(n) {
      var v = parseFloat(n || 0);
      var withDecimals = v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      var noDecimals = v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      var withComma = v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      var noCommaDec = v.toFixed(2);
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/g, withDecimals)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, noCommaDec);
    };
  }

  // Hover / focus / touch-hold cycles through the product's other images (crossfade A<->B).
  function bindGallery(card) {
    var imgA = card.querySelector('.ebc-img-a');
    var imgB = card.querySelector('.ebc-img-b');
    if (!imgA || !imgB) return;
    var gallery = (card.getAttribute('data-gallery') || '').split('|').filter(Boolean);
    if (gallery.length < 2) return;

    var preloaded = false;
    function preload() {
      if (preloaded) return;
      preloaded = true;
      gallery.slice(1).forEach(function (src) { var p = new Image(); p.src = src; });
    }

    var idx = 0, timer = null, activeIsA = true;
    function show(nextSrc) {
      var incoming = activeIsA ? imgB : imgA;
      var outgoing = activeIsA ? imgA : imgB;
      incoming.src = nextSrc;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          incoming.classList.add('is-active');
          outgoing.classList.remove('is-active');
          activeIsA = !activeIsA;
        });
      });
    }
    function start() {
      if (timer) return;
      preload();
      idx = 1 % gallery.length;
      show(gallery[idx]);
      timer = setInterval(function () {
        idx = (idx + 1) % gallery.length;
        show(gallery[idx]);
      }, 1200);
    }
    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
      imgA.classList.add('is-active');
      imgB.classList.remove('is-active');
      activeIsA = true;
    }

    card.addEventListener('mouseenter', start);
    card.addEventListener('mouseleave', stop);
    card.addEventListener('focusin', start);
    card.addEventListener('focusout', stop);

    var holdTimer;
    card.addEventListener('touchstart', function () { holdTimer = setTimeout(start, 180); }, { passive: true });
    card.addEventListener('touchend', function () { clearTimeout(holdTimer); stop(); });
    card.addEventListener('touchcancel', function () { clearTimeout(holdTimer); stop(); });
  }

  // Optional gold quick-add button — only rendered when create({ quickAdd:true }).
  // A product with exactly ONE in-stock variant adds straight to cart (/cart/add.js);
  // a product with 2+ IN-STOCK variants routes to the PDP so the shopper picks a size;
  // a fully sold-out product gets no button. Out-of-stock variants must NOT force the PDP
  // route: a 2-size product whose only buyable size is in stock should quick-add that size
  // directly — otherwise the button reads as a broken add (routes to the PDP for a "choice"
  // that has one option). The whole card stays a link via two sibling <a> overlays, so the
  // button is never an interactive descendant of an <a> (valid markup).
  function quickAddButton(p) {
    var variants = p.variants || [];
    var available = variants.filter(function (x) { return x.available; });
    if (!available.length) return '';            // sold out -> card is a plain link, no button
    var multi = available.length > 1;            // >1 IN-STOCK variant -> choose size on the PDP
    var vid = multi ? '' : (available[0].id || '');  // exactly one buyable variant -> add it directly
    return '<button type="button" class="ebc-qa"' +
      ' aria-label="' + (multi ? 'Choose options' : 'Add to cart') + '"' +
      ' data-qa-href="/products/' + escapeHtml(p.handle) + '"' +
      ' data-qa-multi="' + (multi ? '1' : '0') + '"' +
      (vid ? ' data-qa-id="' + escapeHtml(String(vid)) + '"' : '') + '>' +
      QA_ICONS + '</button>';
  }

  function create(opts) {
    opts = opts || {};
    var money = makeMoney(opts.moneyFormat);
    var quickAdd = !!opts.quickAdd;

    function build(p) {
      var MODEL = window.KM2ProductModel;
      if (!MODEL && window.KM2Deps) window.KM2Deps.get('KM2ProductModel', 'km2-product-card');
      // 700px renditions via the ONE resize helper (D14); the raw src is only the load-order fallback.
      var imgs = (p.images || []).map(function (i) { return MODEL ? MODEL.imageUrl(i.src, 700) : (i.src || ''); });
      var firstImg = imgs[0] || null;
      // The ONE price rule (finding 513): cheapest in-stock size, "From" when dearer sizes exist,
      // compare-at from that same size. variants[0] is only the fallback for a load-order bug.
      var dp = MODEL ? MODEL.displayPrice(p) : null;
      var v = (dp && dp.variant) || (p.variants && p.variants[0]) || {};
      var compare = parseFloat((dp ? dp.compare_at_price : v.compare_at_price) || 0);
      var price = parseFloat((dp ? dp.price : v.price) || 0);
      var shown = dp && dp.from ? fromPrice(money(price)) : money(price);
      var priceHtml = (compare > price)
        ? '<span class="ebc-price-sale">' + shown + '</span><span class="ebc-price-compare">' + money(compare) + '</span>'
        : '<span class="ebc-price">' + shown + '</span>';
      var media = firstImg
        ? '<img class="ebc-img ebc-img-a is-active" src="' + escapeHtml(firstImg) + '" alt="" loading="lazy" draggable="false">' +
          '<img class="ebc-img ebc-img-b" src="" alt="" loading="lazy" draggable="false">'
        : '<div class="ebc-card-placeholder" aria-hidden="true"></div>';
      // Option-2 info (owner 2026-07-03): BRAND (vendor) leads as the bold serif title,
      // product NAME below as the smaller caption, then a price ⇄ type-badge row.
      // Falls back gracefully to a title-led card when vendor is absent (e.g. a rail
      // source that doesn't carry vendor).
      var badge = typeAbbr(p.product_type);
      var vendorLine = p.vendor ? '<h3 class="ebc-card-title">' + escapeHtml(p.vendor) + '</h3>' : '';
      var nameCls = p.vendor ? 'ebc-card-type' : 'ebc-card-title';
      var infoInner =
        vendorLine +
        '<p class="' + nameCls + '">' + escapeHtml(p.title) + '</p>' +
        '<div class="ebc-card-price km2-price-row">' +
          '<span class="km2-price-wrap">' + priceHtml + '</span>' +
          (badge ? '<span class="km2-frag-type">' + escapeHtml(badge) + '</span>' : '') +
        '</div>';
      var href = '/products/' + p.handle;

      var qa = quickAdd ? quickAddButton(p) : '';
      if (qa) {
        // Quick-add variant: div root so the <button> is a sibling of the links, not
        // an interactive descendant of an <a>. The info link carries the accessible
        // name (title/type/price); the media link is a decorative click target.
        var root = document.createElement('div');
        root.className = 'ebc-card ebc-card--qa';
        root.setAttribute('data-gallery', imgs.join('|'));
        root.innerHTML =
          '<div class="ebc-card-media">' + media +
            '<a class="ebc-card__link" href="' + escapeHtml(href) + '" tabindex="-1" aria-hidden="true"></a>' +
            qa +
          '</div>' +
          '<a class="ebc-card__link ebc-card__link--info" href="' + escapeHtml(href) + '">' +
            '<div class="ebc-card-info">' + infoInner + '</div>' +
          '</a>';
        bindGallery(root);
        return root;
      }

      // Default (EBC homepage etc.): unchanged single-<a> card.
      var a = document.createElement('a');
      a.className = 'ebc-card';
      a.href = href;
      a.setAttribute('aria-label', p.title || '');
      a.setAttribute('data-gallery', imgs.join('|'));
      a.innerHTML =
        '<div class="ebc-card-media">' + media + '</div>' +
        '<div class="ebc-card-info">' + infoInner + '</div>';
      bindGallery(a);
      return a;
    }

    return { build: build, money: money };
  }

  // ---- Quick-add cart wiring (one delegated listener; covers loop clones too) -------
  // Reuses the theme's cart plumbing: POST /cart/add.js (window.routes.cart_add_url) and
  // re-renders the header cart bubble from the 'cart-icon-bubble' section — same mechanism
  // product-form.js uses — but WITHOUT opening the cart drawer (owner: just check + count).
  function sectionInner(html) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var sec = doc.querySelector('.shopify-section');
      return sec ? sec.innerHTML : html;
    } catch (e) { return html; }
  }
  function refreshCartCount(sections) {
    var html = sections && sections['cart-icon-bubble'];
    if (!html) return;
    var link = document.getElementById('cart-icon-bubble');
    if (link) link.innerHTML = sectionInner(html);
  }
  // The drawer's HTML is server-rendered at PAGE LOAD; refreshing only the bubble leaves a
  // stale (e.g. empty-state) drawer until the next navigation — found on LIVE 2026-07-02:
  // quick-add 2 items on /, open drawer -> "Your cart is empty" while the bubble says 2.
  // Mirror cart-drawer.js renderContents (replace #CartDrawer from the 'cart-drawer'
  // section + rebind the overlay close), but WITHOUT open() — quick-add stays quiet.
  function refreshCartDrawer(sections) {
    var html = sections && sections['cart-drawer'];
    var target = document.getElementById('CartDrawer');
    if (!html || !target) return;
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('#CartDrawer');
      if (!fresh) return;
      target.innerHTML = fresh.innerHTML;
      var drawerEl = target.closest('cart-drawer') || document.querySelector('cart-drawer');
      // The empty-state is driven by `is-empty` on the OUTER <cart-drawer> (cart-drawer.css:176+),
      // which sits outside #CartDrawer — sync it from the fresh render or the header/items stay
      // display:none even though the swapped-in DOM has the line items.
      var freshDrawer = doc.querySelector('cart-drawer');
      if (drawerEl && freshDrawer) {
        drawerEl.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      }
      var overlay = target.querySelector('#CartDrawer-Overlay');
      if (overlay && drawerEl && typeof drawerEl.close === 'function') {
        overlay.addEventListener('click', drawerEl.close.bind(drawerEl));
      }
    } catch (e) { if (window.console) console.warn('[km2 quick-add] drawer refresh', e); }
  }
  function flash(btn, cls, ms) {
    btn.classList.add(cls);
    setTimeout(function () { btn.classList.remove(cls); }, ms);
  }
  function addToCart(id, btn) {
    if (btn.classList.contains('is-loading')) return;
    btn.classList.remove('is-added', 'is-error');
    btn.classList.add('is-loading');
    // routes.cart_add_url is the FORM endpoint (/cart/add — no .js); for a JSON-body AJAX add
    // the canonical route is /cart/add.js. The bare /cart/add intermittently 503s on theme-dev
    // (SERVICE_UNAVAILABLE, caught in the recently-viewed spec trace 2026-07-02) while the .js
    // endpoint answered 200 consistently — normalize to .js, still honoring a locale-prefixed
    // routes value (e.g. /fr/cart/add -> /fr/cart/add.js).
    var base = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var url = /\.js$/.test(base) ? base : base + '.js';
    // Pages that need more than the bubble refreshed (e.g. the cart page's items + footer)
    // list extra section ids in window.KM2QuickAddSections; the response HTML for all of
    // them rides along and is broadcast via the 'km2:cart:added' event.
    var sections = ['cart-icon-bubble'].concat(window.KM2QuickAddSections || []);
    // Re-render the drawer too whenever the page has one, else it serves page-load-stale HTML.
    if (document.getElementById('CartDrawer')) sections.push('cart-drawer');
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(id), quantity: 1 }],
        sections: sections.join(','),
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok || (res.data && res.data.status)) {
          throw new Error((res.data && res.data.description) || 'Add to cart failed');
        }
        refreshCartCount(res.data.sections);
        refreshCartDrawer(res.data.sections);
        btn.classList.remove('is-loading');
        flash(btn, 'is-added', 1600);
        document.dispatchEvent(new CustomEvent('km2:cart:added', { detail: { sections: res.data.sections || {} } }));
      })
      .catch(function (err) {
        if (window.console) console.warn('[km2 quick-add]', err);
        btn.classList.remove('is-loading');
        flash(btn, 'is-error', 2000);
      });
  }
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('.ebc-qa') : null;
    if (!btn) return;
    e.preventDefault();      // the button sits over the card's stretched link
    e.stopPropagation();
    if (btn.dataset.qaMulti === '1' || !btn.dataset.qaId) {
      if (btn.dataset.qaHref) window.location.href = btn.dataset.qaHref;  // pick a size on the PDP
      return;
    }
    addToCart(btn.dataset.qaId, btn);
  });

  // Server-rendered surfaces (the catalog grid's card-product.liquid) emit EMPTY
  // <button class="ebc-qa" data-qa-*> elements so the state icons stay single-sourced
  // here — hydrate fills them in. Idempotent; safe to call after AJAX re-renders
  // (kmf-filters grid swaps) with an optional root to scope the query.
  function hydrateQuickAdd(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var btns = scope.querySelectorAll('.ebc-qa');
    for (var i = 0; i < btns.length; i++) {
      if (!btns[i].firstElementChild) btns[i].innerHTML = QA_ICONS;
    }
  }
  // KM2Deps.ready: 'interactive' means parsed but DEFERRED SCRIPTS NOT YET RUN, so the old
  // `else` branch fired before the shared globals existed. Only 'complete' runs immediately.
  var __km2boot = function () { hydrateQuickAdd(); };
  (window.KM2Deps ? window.KM2Deps.ready(__km2boot)
    : (document.readyState === 'complete' ? __km2boot()
       : document.addEventListener('DOMContentLoaded', __km2boot, { once: true })));
  // A one-shot pass misses buttons RE-inserted later — e.g. kmf-filters snapshots the
  // server grid's innerHTML BEFORE this script hydrates, clears it, and restores that
  // pre-hydration snapshot on its fallback paths (found on /search 2026-07-02: 3 empty
  // gold circles). Watch for empty .ebc-qa arrivals from ANY inserter, forever.
  var hydrateScheduled = false;
  new MutationObserver(function (muts) {
    if (hydrateScheduled) return;
    for (var m = 0; m < muts.length; m++) {
      var added = muts[m].addedNodes;
      for (var n = 0; n < added.length; n++) {
        var node = added[n];
        if (node.nodeType !== 1) continue;
        var hit = (node.matches && node.matches('.ebc-qa')) ? node
          : (node.querySelector ? node.querySelector('.ebc-qa') : null);
        if (hit && !hit.firstElementChild) {
          hydrateScheduled = true;
          requestAnimationFrame(function () { hydrateScheduled = false; hydrateQuickAdd(); });
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.KM2ProductCard = {
    create: create,
    // The gold quick-add is a cross-surface component (owner 2026-07-02): JS-built
    // cards call quickAddButtonHTML(product) for the full button markup; Liquid-built
    // cards render the empty button and hydrateQuickAdd() supplies the icons.
    quickAddButtonHTML: quickAddButton,
    hydrateQuickAdd: hydrateQuickAdd,
    // The single JS copy of the type-badge map (mirrored in card-product.liquid);
    // kmf-filters.js consumes this so its grid cards match the rails exactly.
    typeAbbr: typeAbbr,
    // The single "From $X" prefix formatter (finding 513); kmf-filters.js and audience-tiles.liquid
    // consume it so a multi-size product reads the same on the grid, the rails and the tiles.
    fromPrice: fromPrice,
    // The canonical HTML escaper (plan 20260901-050844 T3). Promoted from private so there is
    // ONE escaper for card markup rather than a copy per renderer — every string interpolated
    // into card HTML goes through this.
    //
    // The duplicate in assets/kmf-filters.js is deliberately NOT deleted by T0: its surviving
    // caller is the active-filter PILL renderer (`escapeHtml(p.label)`), which is not card code
    // and still needs an escaper. Retiring that copy — and renaming the survivor
    // `escapeHtmlFallback` — is owned by the IMMUTABLE variant-picker plan's T11 (round-3 r3-f3;
    // the card program explicitly declines it).
    escapeHtml: escapeHtml
  };
})();
